import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { handlePhoneClick } from '../utils/trackPhoneCall';
import { siteConfig } from '../config/site';
import PageMeta from '../components/PageMeta';

const ThankYou = () => {
  const { id } = useParams();

  // Dispara a conversão do Google Ads ao carregar a página
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
      console.log('📊 Google Ads: Triggering conversion on thank you page load');
      (window as any).gtag_report_conversion();
    }
  }, []);

  const serviceMap: Record<string, string> = {
    'placa-eletrica': 'Instalação de Placa Elétrica',
    'forno-eletrico': 'Instalação de Forno Elétrico',
    'fogao-eletrico': 'Instalação de Fogão Elétrico',
    'microondas': 'Instalação de Microondas',
    'maquina-loica': 'Instalação de Máquina de Lavar Loiça',
    'maquina-roupa': 'Instalação de Máquina de Lavar Roupa',
    'frigorifico': 'Instalação de Frigorífico',
    'termoacumulador': 'Instalação de Termoacumulador',
  };

  const serviceName = id ? serviceMap[id] || 'Serviço Solicitado' : 'Pedido de Orçamento';

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <PageMeta
        title="Obrigado pelo seu contacto"
        description="Recebemos o seu pedido. A nossa equipa entrará em contacto em breve."
        path="/obrigado"
        noindex
      />
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-indigo-600"></div>
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-50"></div>
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-indigo-50 rounded-full blur-3xl opacity-50"></div>

      <div className="max-w-3xl w-full relative z-10">
        <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="mx-auto w-24 h-24 bg-blue-600 rounded-2xl flex items-center justify-center mb-8 rotate-3 shadow-xl shadow-blue-200">
            <i className="ri-checkbox-circle-fill text-5xl text-white"></i>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">
            Pedido Recebido!
          </h1>
          
          <div className="inline-block px-4 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-bold mb-6">
            {serviceName.toUpperCase()}
          </div>
          
          <p className="text-xl text-gray-600 max-w-xl mx-auto leading-relaxed">
            Obrigado pela sua confiança. Os nossos técnicos já foram notificados e estão a analisar o seu pedido.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Steps Side */}
          <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <i className="ri-list-check text-blue-600"></i>
              Próximos Passos
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center font-bold shrink-0">1</div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">Validação Técnica</h3>
                  <p className="text-xs text-gray-500 mt-1">Analisamos a disponibilidade e os detalhes técnicos da instalação.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center font-bold shrink-0">2</div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">Contacto Telefónico</h3>
                  <p className="text-xs text-gray-500 mt-1">Ligamos para si nos próximos minutos para agendar a visita.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center font-bold shrink-0">3</div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">Execução do Serviço</h3>
                  <p className="text-xs text-gray-500 mt-1">Realizamos a instalação com garantia e profissionalismo.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Side */}
          <div className="bg-gray-900 rounded-3xl p-8 text-white relative overflow-hidden shadow-xl">
            <div className="relative z-10">
              <h2 className="text-xl font-bold mb-4">Precisa de pressa?</h2>
              <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                Se este é um pedido urgente ou se tiver alguma dúvida imediata, fale diretamente com a nossa linha de apoio.
              </p>
              
              <div className="space-y-4">
                <a
                  href={`tel:${siteConfig.phone}`}
                  onClick={handlePhoneClick}
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-2xl text-center transition-all flex items-center justify-center gap-3"
                >
                  <i className="ri-phone-fill"></i>
                  Ligar Agora
                </a>
                <Link
                  to="/"
                  className="block w-full bg-white/10 hover:bg-white/20 text-white font-bold py-4 px-6 rounded-2xl text-center transition-all"
                >
                  Voltar ao Início
                </Link>
              </div>
            </div>
            
            {/* Decorative element for dark card */}
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-blue-600/20 rounded-full blur-2xl"></div>
          </div>
        </div>

        <div className="text-center text-gray-400 text-sm">
          <p>© {new Date().getFullYear()} {siteConfig.name}. Todos os direitos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;


